import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HomePlant } from "../../Services/typescript-fetch-client";
import { ViewMode } from "./ViewMode";
import { RenewPlantType } from "./RenewPlantType";
import { getIsRealTime } from "../../Utils/urlState/getIsRealTime";
import {
    getDateShownFromSearchParams,
    getSearchParams,
    getSearchString,
} from "../../Utils/urlState/searchParams";
import { useDateSelected } from "../../Utils/urlState/useDateSelected";
import { DateShown } from "../../Utils/urlState/DateShown";

/**
 * useUrlState manages the state used by RenewPage that is stored in the page's url, either in the path (view and plantId) or in the search parameters (dateSelected and selectedPlants).
 *
 * It returns both the state's current value, retrieved from the page's url and all needed setters, which work by computing the url corresponding to the new state and navigating to the new url.
 *
 * @param plantType
 * @param homePlants
 * @returns
 */
export const useUrlStateRenew = (
    plantType: RenewPlantType,
    homePlants: HomePlant[]
) => {
    const { plantName: param } = useParams();
    
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // state saved in the the url
    const { view, plantName } = useMemo(() => getViewAndPlantName(param), [param]);
    const dateShown: DateShown = useMemo(
        () => getDateShownFromSearchParams(searchParams),
        [searchParams]
    );
    const selectedPlants = useMemo(
        () => getSelectedPlants(plantName, searchParams, homePlants),
        [plantName, searchParams, homePlants]
    );

    const isRealTime = useMemo(() => getIsRealTime(dateShown), [dateShown]);

    const setUrlState = useCallback(
        (state: {
            view: ViewMode;
            plantName: string | undefined;
            dateShown: DateShown;
            selectedPlants: HomePlant[] | undefined;
        }) => {
            const url = getUrl(state, plantType, homePlants);
            navigate(url);
        },
        [navigate, plantType, homePlants]
    );

    const setView = useCallback(
        (view: ViewMode) => {
            setUrlState({ view, plantName, dateShown, selectedPlants });
        },
        [setUrlState, plantName, dateShown, selectedPlants]
    );

    const state = useMemo(() => {
        return {
            view,
            plantName,
            selectedPlants,
        };
    }, [view, plantName, selectedPlants]);

    const { dateSelected, setDateSelected } = useDateSelected(
        dateShown,
        setUrlState,
        state
    );

    const setSelectedPlants = useCallback(
        (selectedPlants: HomePlant[]) => {
            setUrlState({
                view,
                plantName,
                dateShown,
                selectedPlants,
            });
        },
        [setUrlState, view, plantName, dateShown]
    );

    const navigateToSingles = useCallback(() => {
        const view = ViewMode.Singles;
        const selectedPlants = undefined;
        const plantName = undefined;
        setUrlState({
            view,
            plantName,
            dateShown,
            selectedPlants,
        });
    }, [setUrlState, dateShown]);

    return {
        view,
        setView,
        plantName,
        selectedPlants,
        setSelectedPlants,
        dateSelected,
        setDateSelected,
        dateShown,
        isRealTime,
        navigateToSingles,
    };
};

/**
 * getUrl takes the current state and computes the corresponding url
 * @param state
 * @param plantType
 * @param homePlants
 * @returns
 */
const getUrl = (
    {
        view,
        plantName,
        dateShown,
        selectedPlants,
    }: {
        view: ViewMode;
        plantName: string | undefined;
        dateShown: DateShown;
        selectedPlants: HomePlant[] | undefined;
    },
    plantType: HomePlant.TypeEnum,
    homePlants: HomePlant[]
) => {
    const searchParams = getSearchParams(dateShown);
    if (view !== ViewMode.Single) {
        if (
            Array.isArray(selectedPlants) &&
            selectedPlants.length !== homePlants.length
        ) {
            searchParams.set(
                "plants",
                selectedPlants.map((p) => p.id).join(",")
            );
        }
    }

    const query = getSearchString(searchParams);

    switch (view) {
        case ViewMode.Singles:
            return "/" + plantType + "/all" + query;
        case ViewMode.Single:
            return "/" + plantType + "/" + plantName + query;
        case ViewMode.Region:
            return "/" + plantType + "/zone" + query;
        default:
            return "/" + plantType + query;
    }
};

/** compute the ViewMode from the current value of the `plantId` path parameter */
const getView = (param: string | undefined): ViewMode => {
    if (!param) {
        return ViewMode.Total;
    } else if (param === "all") {
        return ViewMode.Singles;
    } else if (param === "zone") {
        return ViewMode.Region;
    } else {
        return ViewMode.Single;
    }
};

/** compute the view and plantId from the current value of the `plantId` path parameter. plantId is a number if we are in single plant mode, otherwise it is undefined. */
const getViewAndPlantName = (
    param: string | undefined
): { view: ViewMode; plantName?: string } => {
    const view = getView(param);
    if (view === ViewMode.Single) {
        const plantName = param;
        return { view, plantName };
    } else {
        return { view };
    }
};

const getSelectedPlants = (
    plantName: string | undefined,
    searchParams: URLSearchParams,
    homePlants: HomePlant[]
): HomePlant[] => {
    if (typeof plantName === "string") {
        return homePlants.filter((plant) => plant.plantName === plantName);
    }
    const plants = searchParams.get("plants");
    if (typeof plants !== "string") {
        return homePlants;
    }
    const ids = new Set(
        plants
            .split(",")
            .map((id) => Number.parseInt(id, 10))
            .filter(Number.isInteger)
    );
    return homePlants.filter((plant) => ids.has(plant.id!));
};
